import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { initAnalytics } from '../lib/analytics';

const STORAGE_KEY = 'vsp_cookie_consent';

/**
 * @typedef {'all' | 'essential'} ConsentChoice
 */

/**
 * Read the persisted consent choice from localStorage.
 * @returns {ConsentChoice | null}
 */
export function getConsentChoice() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === 'all' || raw === 'essential') {
      return raw;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Check whether the user has consented to analytics cookies.
 * @returns {boolean}
 */
export function hasAnalyticsConsent() {
  return getConsentChoice() === 'all';
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const choice = getConsentChoice();

    if (choice === null) {
      // First visit: show banner after a short delay for slide-up effect
      const timer = setTimeout(() => setVisible(true), 500);
      return () => clearTimeout(timer);
    }

    // Returning user who already consented to all: initialize GA4
    if (choice === 'all') {
      initAnalytics();
    }
  }, []);

  const handleAcceptAll = useCallback(() => {
    try {
      localStorage.setItem(STORAGE_KEY, 'all');
    } catch {
      // localStorage may be unavailable
    }
    initAnalytics();
    setVisible(false);
  }, []);

  const handleEssentialOnly = useCallback(() => {
    try {
      localStorage.setItem(STORAGE_KEY, 'essential');
    } catch {
      // localStorage may be unavailable
    }
    setVisible(false);
  }, []);

  if (!visible) {
    return null;
  }

  return (
    <div
      className="fixed bottom-0 inset-x-0 z-50 animate-slide-up"
      role="dialog"
      aria-label="쿠키 동의"
    >
      <div className="mx-auto max-w-4xl px-4 pb-4">
        <div className="rounded-2xl border border-border bg-card shadow-lg p-5 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            {/* Text */}
            <div className="flex-1 text-sm text-card-foreground">
              <p className="font-semibold mb-1">
                이 사이트는 쿠키를 사용합니다
              </p>
              <p className="text-muted-foreground leading-relaxed">
                서비스 운영에 필요한 필수 쿠키와 사용 경험 개선을 위한 분석
                쿠키를 사용합니다. 분석 쿠키는 동의한 경우에만 활성화됩니다.{' '}
                <Link
                  to="/privacy"
                  className="underline underline-offset-2 hover:text-foreground transition-colors"
                >
                  개인정보처리방침
                </Link>
              </p>
            </div>

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 shrink-0">
              <button
                type="button"
                onClick={handleEssentialOnly}
                className="inline-flex items-center justify-center rounded-lg border border-border bg-background px-5 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                필수 쿠키만
              </button>
              <button
                type="button"
                onClick={handleAcceptAll}
                className="inline-flex items-center justify-center rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              >
                모두 허용
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
